import { useDisclosure } from '@mantine/hooks';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ContextProp,
  GroupedVirtuoso,
  GroupedVirtuosoHandle,
  Virtuoso,
  VirtuosoHandle,
} from 'react-virtuoso';
import { EventAccordionItemBodyComponentType } from 'src/components/Accordions';
import { useInventoryEventLocalContext } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/InventoryEventLocalContext';
import { EventHeaderWithNavMobile } from 'src/components/Listings/InventoryEventPage/InventoryEventPageRework/Mobile/EventHeader/EventHeaderWithNavMobile';
import { InventoryEventTabs } from 'src/components/Listings/InventoryEventPage/InventoryEventTabs';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useLocalEventMobileScrollContext } from 'src/contexts/MobileSortAndFilterContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import {
  ActionOutboxEntityType,
  CompListing,
  Event,
  EventWithData,
  Listing,
  ListingGroup,
  SectionInfo,
  Venue,
} from 'src/WebApiController';

import { BulkActionsDrawer } from '../BulkActionsContent';
import { PriceRangeGroupItem } from './CompListingTile';
import { ListingGroupTile } from './ListingGroupTile';
import { ListingTile } from './ListingTile';
import { ListingTilesBulkActions } from './ListingTilesBulkActions';
import { useListingBulkActions } from './ListingTilesBulkActions.hooks';
import {
  InventoryListingTileWrapper,
  ListingTilesFloatingActions,
} from './ListingTilesV2.components';
import {
  useGroupedVirtuoso,
  useInventoryListings,
} from './ListingTilesV2.hooks';
import { InventoryMetrics, ListingTileItem } from './ListingTilesV2.types';
import { isPriceRangeGroup } from './ListingTilesV2.utils';
import { ListingTilesVenueMap } from './ListingTilesVenueMap';

export type ListingTilesV2Props = EventAccordionItemBodyComponentType<Listing>;

type ListingTilesVirtuosoContext = {
  showHeader: boolean;
  event: EventWithData;
  venue: Venue;
  metrics: InventoryMetrics;
  entities: Listing[];
  compListings: CompListing[];
  selectedSections: SectionInfo[];
  setSelectedSections: React.Dispatch<React.SetStateAction<SectionInfo[]>>;
};

const Header = ({ context }: ContextProp<ListingTilesVirtuosoContext>) => {
  return (
    <Stack direction="column">
      {context.showHeader && (
        <ListingTilesVenueMap
          eventListings={context.entities}
          compListings={context.compListings}
          selectedSections={context.selectedSections}
          onUpdateSelectedSections={context.setSelectedSections}
        />
      )}
      <div style={{ padding: `0 ${vars.spacing.m}` }}>
        <EventHeaderWithNavMobile
          event={context.event}
          venue={context.venue}
          inventoryMetrics={context.metrics}
        />
      </div>
    </Stack>
  );
};

export const ListingTilesV2 = ({
  entities,
  event,
  entityCount,
}: ListingTilesV2Props) => {
  const [selectedSections, setSelectedSections] = useState<SectionInfo[]>([]);
  const { activeTab, showMap } = useInventoryEventLocalContext();
  const { setSelectionMode, itemSelection } = useMultiSelectionContext();
  const [showBulkActions, { toggle: toggleBulkActions }] = useDisclosure();

  useEffect(() => {
    // Reset section selecteds when hide Map
    if (!showMap) {
      setSelectedSections([]);
    }
  }, [showMap]);

  const { data } = useCatalogDataContext();
  const { eventWithData, venue } = useMemo(() => {
    const ev = data!.events[event.virtId];
    const venue = ev.event && data!.venues[ev.event.venId];

    return { eventWithData: ev, venue };
  }, [data, event.virtId]);

  const { compListingsQuery } = useGetCompListings(event);
  const listingActionGroups = useListingBulkActions(eventWithData);

  const {
    ownInventory,
    allInventory,
    allInventoryWithGroups,
    inventoryMetrics,
  } = useInventoryListings(
    entities,
    compListingsQuery.data ?? [],
    selectedSections
  );

  const virtuosoContext = useMemo(
    (): ListingTilesVirtuosoContext => ({
      showHeader: showMap,
      event: eventWithData,
      venue: venue,
      metrics: inventoryMetrics,
      entities: entities,
      compListings: compListingsQuery.data ?? [],
      selectedSections: selectedSections,
      setSelectedSections: setSelectedSections,
    }),
    [
      compListingsQuery.data,
      entities,
      eventWithData,
      inventoryMetrics,
      selectedSections,
      showMap,
      venue,
    ]
  );

  if (isEmpty(entities)) {
    // Only show a loading spinner when the server-reported count is non-zero
    // (listings haven't arrived yet). For a zero-listing event, entityCount is
    // 0 and entities is permanently [], so we skip the spinner entirely.
    if (entityCount !== 0) {
      return <PosSpinner size={vars.iconSize.s} />;
    }
    return null;
  }

  return (
    <>
      {activeTab === InventoryEventTabs.Info ? (
        <MyInventoryTiles
          event={event}
          entities={entities}
          inventory={ownInventory}
          context={virtuosoContext}
        />
      ) : (
        <AllInventoryTiles
          event={event}
          entities={entities}
          inventory={allInventoryWithGroups}
          context={virtuosoContext}
        />
      )}
      <ListingTilesBulkActions
        eventWithData={eventWithData}
        onBulkActionsClick={toggleBulkActions}
      />
      <ListingTilesFloatingActions allInventory={allInventory} />
      <BulkActionsDrawer
        entityType={ActionOutboxEntityType.Listing}
        actionGroups={listingActionGroups}
        onClose={() => {
          setSelectionMode(undefined);
          toggleBulkActions();
        }}
        show={!isEmpty(itemSelection) && showBulkActions}
      />
    </>
  );
};

const MyInventoryTiles = ({
  inventory,
  entities,
  event,
  context,
}: {
  inventory: Listing[];
  event: Event;
  entities: Listing[];
  context: ListingTilesVirtuosoContext;
}) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const { showMap } = useInventoryEventLocalContext();
  const { setIsAtTop, setIsAtBottom } = useLocalEventMobileScrollContext();
  useEffect(() => {
    // Scrolll to venue map by default
    if (showMap) {
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            index: 0,
            align: 'end', // We want to scroll to the map, which is in the header, this is work around
            behavior: 'smooth',
          }),
        300
      );
    }
  }, [showMap]);
  return (
    <Virtuoso<Listing, ListingTilesVirtuosoContext>
      ref={virtuosoRef}
      data={inventory}
      components={{ Header: Header }}
      context={context}
      itemContent={(_, listing) => {
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            {listing.isLtGrp ? (
              <ListingGroupTile
                key={listing.id}
                listingGroup={listing as ListingGroup}
                event={event}
                entities={entities}
              />
            ) : (
              <ListingTile
                key={listing.id}
                listing={listing}
                event={event}
                entities={entities}
              />
            )}
          </div>
        );
      }}
      atTopStateChange={setIsAtTop}
      atBottomStateChange={setIsAtBottom}
    />
  );
};

const AllInventoryTiles = ({
  inventory,
  entities,
  event,
  context,
}: {
  inventory: ListingTileItem[];
  event: Event;
  entities: Listing[];
  context: ListingTilesVirtuosoContext;
}) => {
  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const { showMap } = useInventoryEventLocalContext();
  const { setIsAtTop, setIsAtBottom } = useLocalEventMobileScrollContext();

  useEffect(() => {
    // Scrolll to venue map by default
    if (showMap) {
      setTimeout(
        () =>
          virtuosoRef.current?.scrollToIndex({
            groupIndex: 0,
            align: 'end', // We want to scroll to the map, which is in the header, this is work around
            behavior: 'smooth',
          }),
        300
      );
    }
  }, [showMap]);

  const { localGroupCounts, onUpdateExpanded, getGroupItemIndexFromItemIndex } =
    useGroupedVirtuoso(inventory);

  return (
    <GroupedVirtuoso<ListingTileItem, ListingTilesVirtuosoContext>
      ref={virtuosoRef}
      components={{ Header: Header }}
      context={context}
      groupCounts={localGroupCounts}
      groupContent={(groupIndex) => {
        return (
          <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
            <InventoryListingTileWrapper
              listing={inventory[groupIndex]}
              event={event}
              entities={entities}
              expanded={localGroupCounts[groupIndex] > 0}
              onUpdateExpanded={onUpdateExpanded(groupIndex)}
            />
          </div>
        );
      }}
      itemContent={(itemIndex, groupIndex) => {
        const group = inventory[groupIndex];
        if (!isPriceRangeGroup(group)) {
          return null;
        }
        const groupItemIndex = getGroupItemIndexFromItemIndex(
          itemIndex,
          groupIndex
        );
        const item = group.groupItems[groupItemIndex];
        if (item) {
          return (
            <div style={{ padding: `0 ${vars.spacing.m} ${vars.spacing.xxs}` }}>
              <PriceRangeGroupItem item={item} />
            </div>
          );
        }
      }}
      atTopStateChange={setIsAtTop}
      atBottomStateChange={setIsAtBottom}
    />
  );
};
